// The band's FEEL, as a tune's arrangement remembers it (docs/swing-plan.md
// §4–5): where each part sits against the kit, how the melody articulates a
// swing line, and how the walking bass accents and skips. These are band
// fields (style.ts), so they ride per-tune memory, sections, house defaults
// and playlist calls with the rest of the groove. The vocabulary lives here,
// apart from the playback maths in audio/feel.ts, so the music model never
// imports the audio layer.

import { normalizeSwingOffset } from './swingFeel';

/** Where a part sits against the kit: ahead of it, on it, or laid back. */
export type Placement = 'on-top' | 'centre' | 'behind';
export const PLACEMENTS: readonly Placement[] = ['on-top', 'centre', 'behind'];

/** How the melody plays a swing line: as written (even, legato), or phrased
 *  the way a horn player swings it (long downbeats, light upbeats, phrase
 *  ends clipped). Phrasing applies only where the band swings. */
export type MelodyPhrasing = 'written' | 'swing';
export const MELODY_PHRASINGS: readonly MelodyPhrasing[] = ['written', 'swing'];

/** Which beats of the bar the walking bass leans on. Drummers differ (one
 *  accents 2 and 4 on the ride, one 1 and 3, one none), so it is a choice. */
export type BassAccent = 'even' | 'two-four' | 'one-three';
export const BASS_ACCENTS: readonly BassAccent[] = ['even', 'two-four', 'one-three'];

export function isPlacement(value: unknown): value is Placement {
  return typeof value === 'string' && (PLACEMENTS as readonly string[]).includes(value);
}

export function isMelodyPhrasing(value: unknown): value is MelodyPhrasing {
  return typeof value === 'string' && (MELODY_PHRASINGS as readonly string[]).includes(value);
}

export function isBassAccent(value: unknown): value is BassAccent {
  return typeof value === 'string' && (BASS_ACCENTS as readonly string[]).includes(value);
}

/** The feel fields of a band and what a band stored before them plays. */
export interface BandFeelFields {
  swingWithKit: boolean;
  melodyPlacement: Placement;
  melodyPhrasing: MelodyPhrasing;
  bassPlacement: Placement;
  bassAccent: BassAccent;
  bassSkips: boolean;
  compPlacement: Placement;
  /** How much the band swings against its kit under the Kit choice: added to
   *  the kit's measured ratio (swingFeel.ts `kitSwingRatio`). 0 is the kit's. */
  swingOffset: number;
  /** How much each part swings against the band, under the Kit choice. */
  melodySwingOffset: number;
  compSwingOffset: number;
  bassSwingOffset: number;
}

/** A swinging band's defaults: the melody laid back with its downbeats, the
 *  bass a hair ahead, the comp behind, the line phrased, 2 and 4 leaned on,
 *  and the walking line skipping into the beat as often as recorded bassists
 *  do at the tune's tempo (user ruling Oct 2026, docs/bass-feel.md). */
export const SWING_BAND_FEEL: BandFeelFields = {
  swingWithKit: true,
  melodyPlacement: 'behind',
  melodyPhrasing: 'swing',
  bassPlacement: 'centre',
  bassAccent: 'two-four',
  bassSkips: true,
  compPlacement: 'behind',
  swingOffset: 0,
  melodySwingOffset: 0,
  compSwingOffset: 0,
  bassSwingOffset: 0,
};

/** A straight band's (Latin, funk, even eighths): every part centred, the
 *  melody as written, the bass even. */
export const STRAIGHT_BAND_FEEL: BandFeelFields = {
  swingWithKit: true,
  melodyPlacement: 'centre',
  melodyPhrasing: 'written',
  bassPlacement: 'centre',
  bassAccent: 'even',
  bassSkips: false,
  compPlacement: 'centre',
  swingOffset: 0,
  melodySwingOffset: 0,
  compSwingOffset: 0,
  bassSwingOffset: 0,
};

export const BAND_FEEL_KEYS = [
  'swingWithKit',
  'melodyPlacement',
  'melodyPhrasing',
  'bassPlacement',
  'bassAccent',
  'bassSkips',
  'compPlacement',
  'swingOffset',
  'melodySwingOffset',
  'compSwingOffset',
  'bassSwingOffset',
] as const satisfies readonly (keyof BandFeelFields)[];

/** The band's swing offsets, a number each. */
export const SWING_OFFSET_KEYS = [
  'swingOffset',
  'melodySwingOffset',
  'compSwingOffset',
  'bassSwingOffset',
] as const satisfies readonly (keyof BandFeelFields)[];

/** The valid feel fields of an untrusted object; anything else is dropped. */
export function normalizeBandFeel(raw: Record<string, unknown>): Partial<BandFeelFields> {
  const out: Partial<BandFeelFields> = {};
  if (typeof raw.swingWithKit === 'boolean') out.swingWithKit = raw.swingWithKit;
  if (typeof raw.bassSkips === 'boolean') out.bassSkips = raw.bassSkips;
  if (isPlacement(raw.melodyPlacement)) out.melodyPlacement = raw.melodyPlacement;
  if (isPlacement(raw.bassPlacement)) out.bassPlacement = raw.bassPlacement;
  if (isPlacement(raw.compPlacement)) out.compPlacement = raw.compPlacement;
  if (isMelodyPhrasing(raw.melodyPhrasing)) out.melodyPhrasing = raw.melodyPhrasing;
  if (isBassAccent(raw.bassAccent)) out.bassAccent = raw.bassAccent;
  for (const key of SWING_OFFSET_KEYS) {
    const offset = normalizeSwingOffset(raw[key]);
    if (offset !== undefined) out[key] = offset;
  }
  return out;
}

/**
 * A stored band with every feel field: one saved before the fields existed
 * plays the defaults for its own kind (a swinging band the swing defaults, a
 * straight one the straight ones), never the last tune's values.
 */
export function withBandFeel<T extends { bassSwing: boolean } & Partial<BandFeelFields>>(
  band: T,
): T & BandFeelFields {
  const defaults = band.bassSwing ? SWING_BAND_FEEL : STRAIGHT_BAND_FEEL;
  return {
    ...band,
    ...Object.fromEntries(
      BAND_FEEL_KEYS.map((key) => [key, band[key] === undefined ? defaults[key] : band[key]]),
    ),
  } as T & BandFeelFields;
}
